import { decodeFiltersSearchParam } from '@app/logic/filters';
import { useLookbookProducts } from '@app/services/hooks/lookbook/useLookbookProducts';
import { GAProductListItemId, GAProductListItemName, mapProductsToGAItems, useGTM } from '@app/utils/analytics';
import { isDefined } from '@app/utils/is';
import { isEmpty } from '@app/utils/isEmpty';
import { omit } from '@app/utils/omit';
import { Filter, Product, ProductFilters } from '@typings';
import { AxiosError } from 'axios';
import React from 'react';

import { useLookbookContext } from './LookbookContext';
import { useUrlProductFilters } from './useUrlProductFilters';

interface Context {
  productAttributes: Filter.Field<string>[];
  pageCount: number;
  sortedProducts: Product.Full[];
  totalItems: number;
  isError: boolean;
  error: AxiosError<Responses.Errors> | null;
  isLoading: boolean;
  isLoadingFilters: boolean;
  isLoadingNextPage: boolean;
  fetchNextPage: VoidFunction;
  hasNextPage: boolean;
  filters: ProductFilters;
  handleFilterChange: (newFilters: ProductFilters, preserveRest?: boolean) => void;
  handleClearAllFilters: VoidFunction;
  handleClearSearch: VoidFunction;
  handleClearFilters: VoidFunction;
  expandedFilters: Record<string, boolean>;
  setExpandedFilters: (expandedState: Record<string, boolean>) => void;
}

export const useLookbookProductsContext = (): Context => {
  const context = React.useContext(LookbookProductsContext);
  if (context === null) {
    throw new Error('LookbookProductsContext can not be used outside the scope of LookbookProductsContextProvider');
  }

  return context;
};

const LookbookProductsContext = React.createContext<Context | null>(null);
interface Props {
  productId: string | undefined;
}
export const LookbookProductsContextProvider = ({ children, productId }: React.WithChildren<Props>) => {
  const { lookbookId, selectedPricelist } = useLookbookContext();
  const filtersFromUrl = decodeFiltersSearchParam(window.location.search);
  const [filters, setFilters] = React.useState<ProductFilters>(filtersFromUrl);
  const [expandedFilters, setExpandedFilters] = React.useState<Record<string, boolean>>({});
  const { trackGTMEvent } = useGTM();

  const { fetchNextPage, isFetching, isLoading, isPlaceholderData, isFetchingNextPage, data, isError, error, hasNextPage } =
    useLookbookProducts({ filters, lookbookId, pricelistId: selectedPricelist?.id });

  const handleSetFiltersToUrl = useUrlProductFilters();

  const { productAttributes, currentPage, sortedProducts, totalItems } = React.useMemo(
    () => ({
      currentPage: data?.currentPage ?? 0,
      productAttributes: data?.attributes ?? [],
      sortedProducts: data?.sortedProducts ?? [],
      totalItems: data?.totalItems ?? 0,
    }),
    [data],
  );

  React.useEffect(() => {
    if (!data?.lastPageProducts || isEmpty(data.lastPageProducts)) {
      return;
    }

    const globalIndexOffset = (data.currentPage - 1) * data.limit;
    const items = mapProductsToGAItems(data.lastPageProducts, { globalIndexOffset });
    trackGTMEvent('view_item_list', {
      ecommerce: {
        item_list_id: GAProductListItemId.Lookbook,
        item_list_name: GAProductListItemName.Lookbook,
        items,
      },
    });
  }, [data?.currentPage, data?.lastPageProducts, data?.limit, lookbookId, trackGTMEvent]);

  React.useEffect(() => {
    const firstProductAttributeField = productAttributes[0]?.field;
    if (!isEmpty(expandedFilters) || !isDefined(firstProductAttributeField)) {
      return;
    }

    setExpandedFilters({ [firstProductAttributeField]: true });
  }, [expandedFilters, productAttributes]);

  const isLoadingProducts = (isFetching && isPlaceholderData) || isLoading;
  const isLoadingFilters = isEmpty(expandedFilters) && isLoadingProducts;

  React.useEffect(() => {
    if (!isDefined(productId) || !hasNextPage) {
      return;
    }

    const currentIndex = sortedProducts.findIndex(product => product.product === productId);
    if (currentIndex === -1) {
      return;
    }

    if (currentIndex > sortedProducts.length - 2) {
      fetchNextPage();
    }
  }, [fetchNextPage, productId, sortedProducts, hasNextPage]);

  const handleFilterChange = (newFilters: ProductFilters, preserveRest: boolean = true) => {
    const nonEmptyNewFilters = Object.entries(newFilters).reduce((acc, [key, value]) => {
      if (isEmpty(value as Array<string>) || !isDefined(value)) {
        return acc;
      }

      return { ...acc, [key]: value };
    }, {});

    if (!preserveRest) {
      handleSetFiltersToUrl({ ...nonEmptyNewFilters });
      setFilters(nonEmptyNewFilters);

      return;
    }

    const nonEmptyPreviousFilters = Object.entries(filters).reduce((acc, [key, value]) => {
      if (key in newFilters || !isDefined(value)) {
        return acc;
      }

      return { ...acc, [key]: value };
    }, {});

    handleSetFiltersToUrl({ ...nonEmptyPreviousFilters, ...nonEmptyNewFilters });
    setFilters({ ...nonEmptyPreviousFilters, ...nonEmptyNewFilters });
  };

  const handleClearAllFilters = () => {
    setFilters({});
    handleSetFiltersToUrl({});
  };

  const handleClearFilters = () => {
    const { search } = filters;

    setFilters({ search: search ?? '' });
    handleSetFiltersToUrl({ search: search ?? '' });
  };

  const handleClearSearch = () => {
    const newFilters = omit(filters, ['search']);

    setFilters(newFilters);
    handleSetFiltersToUrl(newFilters);
  };

  return (
    <LookbookProductsContext.Provider
      value={{
        error,
        expandedFilters,
        fetchNextPage,
        filters,
        handleClearAllFilters,
        handleClearFilters,
        handleClearSearch,
        handleFilterChange,
        hasNextPage,
        isError,
        isLoading: isLoadingProducts,
        isLoadingFilters,
        isLoadingNextPage: isFetchingNextPage,
        pageCount: currentPage,
        productAttributes,
        setExpandedFilters,
        sortedProducts,
        totalItems,
      }}
    >
      {children}
    </LookbookProductsContext.Provider>
  );
};
